import { supabase } from '../lib/supabase';

class BatchManagementService {
  // Freeze/unfreeze upload functionality
  async setUploadFreeze(freezeStatus) {
    try {
      const { data, error } = await supabase?.rpc('set_upload_freeze', { 
        freeze_status: freezeStatus 
      });

      if (error) {
        throw error;
      }

      return { success: true, data: freezeStatus };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // Check if uploads are frozen - Updated to use the correct function
  async isUploadFrozen() {
    try {
      const { data, error } = await supabase?.rpc('get_upload_freeze');

      if (error) {
        throw error;
      }

      return { success: true, data: data || false };
    } catch (error) {
      return { success: false, error: error?.message, data: false };
    }
  }

  // NEW HOTFIX METHODS:

  // Get batch stage health (using new view)
  async getBatchStageHealth(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('v_batch_stage_health')
        ?.select('*')
        ?.eq('batch_id', batchId)
        ?.single();

      if (error) {
        throw error;
      }

      return { success: true, data: data || {} };
    } catch (error) {
      return { success: false, error: error?.message, data: {} };
    }
  }

  // Get all batch stage health
  async getAllBatchStageHealth() {
    try {
      const { data, error } = await supabase
        ?.from('v_batch_stage_health')
        ?.select('*')
        ?.order('filename', { ascending: true });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Enhanced requeue with custom timeout
  async requeueStuckBatchesWithTimeout(maxAgeMinutes = 10) {
    try {
      const { data, error } = await supabase?.rpc('requeue_stuck_batches', {
        max_age_minutes: maxAgeMinutes
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Replay batch safely (idempotent)
  async replayBatchSafely(batchId) {
    try {
      const { data, error } = await supabase?.rpc('replay_batch_safely', {
        target_batch_id: batchId
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Run watchdog
  async runWatchdog() {
    try {
      const { data, error } = await supabase?.rpc('watchdog_stuck_batches');

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Inspect batch health (detailed analysis)
  async inspectBatchHealth(batchId) {
    try {
      const { data, error } = await supabase?.rpc('inspect_batch_health', {
        target_batch_id: batchId
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Promote batch persons (manual trigger)
  async promoteBatchPersons(batchId) {
    try {
      const { data, error } = await supabase?.rpc('promote_batch_persons', {
        target_batch_id: batchId
      });

      if (error) {
        throw error;
      }

      const result = Array.isArray(data) ? data?.[0] : data;
      
      return { 
        success: true, 
        data: {
          persons_created: parseInt(result?.persons_created || 0),
          persons_existing: parseInt(result?.persons_existing || 0),
          persons_skipped: parseInt(result?.persons_skipped || 0)
        }
      };
    } catch (error) {
      return { success: false, error: error?.message, data: {} };
    }
  }

  // Promote batch arrests (manual trigger)  
  async promoteBatchArrests(batchId) {
    try {
      const { data, error } = await supabase?.rpc('promote_batch_arrests', {
        target_batch_id: batchId
      });

      if (error) {
        throw error;
      }

      const result = Array.isArray(data) ? data?.[0] : data;
      
      return { 
        success: true, 
        data: {
          arrests_created: parseInt(result?.arrests_created || 0),
          arrests_existing: parseInt(result?.arrests_existing || 0),
          arrests_skipped: parseInt(result?.arrests_skipped || 0)
        }
      };
    } catch (error) {
      return { success: false, error: error?.message, data: {} };
    }
  }

  // Get batches with promotion issues (for dashboard)
  async getBatchesWithIssues() {
    try {
      const { data, error } = await supabase
        ?.from('v_batch_stage_health')
        ?.select('*')
        ?.neq('health_status', 'PROMOTION_READY')
        ?.order('raw_rows', { ascending: false });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Get promotion readiness summary
  async getPromotionReadinessSummary() {
    try {
      const { data, error } = await supabase
        ?.from('v_batch_stage_health')
        ?.select(`
          health_status,
          COUNT(*) as batch_count,
          SUM(raw_rows) as total_raw_rows,
          SUM(promotable_persons) as total_promotable_persons,
          SUM(promotable_arrests) as total_promotable_arrests
        `)
        ?.neq('status', 'completed');

      if (error) {
        throw error;
      }

      // Process the grouped results
      const summary = {
        promotionReady: 0,
        poorNames: 0,
        poorDates: 0,
        poorFacilities: 0,
        noData: 0,
        totalBatches: 0,
        totalRawRows: 0,
        totalPromotablePersons: 0,
        totalPromotableArrests: 0
      };

      (data || [])?.forEach(item => {
        summary.totalBatches += parseInt(item?.batch_count || 0);
        summary.totalRawRows += parseInt(item?.total_raw_rows || 0);
        summary.totalPromotablePersons += parseInt(item?.total_promotable_persons || 0);
        summary.totalPromotableArrests += parseInt(item?.total_promotable_arrests || 0);

        switch (item?.health_status) {
          case 'PROMOTION_READY':
            summary.promotionReady += parseInt(item?.batch_count || 0);
            break;
          case 'POOR_NAMES':
            summary.poorNames += parseInt(item?.batch_count || 0);
            break;
          case 'POOR_DATES':
            summary.poorDates += parseInt(item?.batch_count || 0);
            break;
          case 'POOR_FACILITIES':
            summary.poorFacilities += parseInt(item?.batch_count || 0);
            break;
          case 'NO_DATA':
            summary.noData += parseInt(item?.batch_count || 0);
            break;
        }
      });

      return { success: true, data: summary };
    } catch (error) {
      return { success: false, error: error?.message, data: {} };
    }
  }

  // Get stuck batches
  async getStuckBatches() {
    try {
      const { data, error } = await supabase
        ?.from('upload_batches')
        ?.select(`
          id,
          filename,
          status,
          updated_at,
          last_activity_at,
          stuck_since,
          requeue_count,
          rows_in_file,
          rows_inserted,
          error_text
        `)
        ?.or('is_stuck.eq.true,status.eq.stuck')
        ?.order('updated_at', { ascending: true });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Detect stuck batches
  async detectStuckBatches() {
    try {
      const { data, error } = await supabase?.rpc('detect_stuck_batches');

      if (error) {
        throw error;
      }

      return { success: true, data: { stuckCount: data || 0 } };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // Requeue all stuck batches
  async requeueStuckBatches() {
    try {
      const { data, error } = await supabase?.rpc('requeue_stuck_batches');

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Requeue specific batch
  async requeueBatch(batchId) {
    try {
      const { data, error } = await supabase?.rpc('requeue_batch', { 
        target_batch_id: batchId 
      });

      if (error) {
        throw error;
      }

      return { success: true, data: data || false };
    } catch (error) {
      return { success: false, error: error?.message, data: false };
    }
  }

  // Get comprehensive batch diagnostics
  async getBatchDiagnostics() {
    try {
      const { data, error } = await supabase?.rpc('get_batch_diagnostics');

      if (error) {
        throw error;
      }

      const diagnostics = data?.[0] || {};
      
      return { 
        success: true, 
        data: {
          totalBatches: parseInt(diagnostics?.total_batches || 0),
          queuedBatches: parseInt(diagnostics?.queued_batches || 0),
          processingBatches: parseInt(diagnostics?.processing_batches || 0),
          stuckBatches: parseInt(diagnostics?.stuck_batches || 0),
          failedBatches: parseInt(diagnostics?.failed_batches || 0),
          completedBatches: parseInt(diagnostics?.completed_batches || 0),
          avgProcessingTime: diagnostics?.avg_processing_time || null,
          oldestStuckBatch: diagnostics?.oldest_stuck_batch || null,
          totalDeadletterRows: parseInt(diagnostics?.total_deadletter_rows || 0)
        }
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // Get batch progression analysis
  async getBatchProgression(batchId) {
    try {
      const { data, error } = await supabase?.rpc('count_batch_progression', { 
        target_batch_id: batchId 
      });

      if (error) {
        throw error;
      }

      const progression = data?.[0] || {};
      
      return { 
        success: true, 
        data: {
          filename: progression?.batch_filename || '',
          rawRows: parseInt(progression?.raw_rows || 0),
          finalPersons: parseInt(progression?.final_persons || 0),
          finalArrests: parseInt(progression?.final_arrests || 0),
          deadletterRows: parseInt(progression?.deadletter_rows || 0),
          progressionHealth: progression?.progression_health || 'Unknown'
        }
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // Get deadletter records for a batch
  async getDeadletterRecords(batchId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('ingest_deadletter')
        ?.select('*')
        ?.eq('batch_id', batchId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Get recent processing activity
  async getRecentActivity(hours = 24) {
    try {
      const { data, error } = await supabase
        ?.from('upload_batches')
        ?.select(`
          id,
          filename,
          status,
          upload_date,
          completed_at,
          rows_in_file,
          rows_inserted,
          rows_errors,
          requeue_count
        `)
        ?.gte('upload_date', new Date(Date.now() - hours * 60 * 60 * 1000)?.toISOString())
        ?.order('upload_date', { ascending: false })
        ?.limit(100);

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error?.message, data: [] };
    }
  }

  // Get processing health summary
  async getProcessingHealthSummary() {
    try {
      // Get basic stats
      const diagnostics = await this.getBatchDiagnostics();
      if (!diagnostics?.success) {
        throw new Error(diagnostics?.error);
      }

      // Get recent failures
      const { data: recentFailures, error: failuresError } = await supabase
        ?.from('upload_batches')
        ?.select('id, filename, error_text, updated_at')
        ?.eq('status', 'failed')
        ?.gte('updated_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString())
        ?.order('updated_at', { ascending: false })
        ?.limit(10);

      if (failuresError) {
        throw failuresError;
      }

      // Calculate health score
      const stats = diagnostics?.data;
      const totalActive = stats?.queuedBatches + stats?.processingBatches + stats?.stuckBatches;
      const healthScore = totalActive > 0 ? 
        Math.max(0, 100 - (stats?.stuckBatches * 30 + stats?.failedBatches * 10)) : 100;

      return {
        success: true,
        data: {
          ...stats,
          recentFailures: recentFailures || [],
          healthScore,
          status: healthScore >= 90 ? 'healthy' : healthScore >= 70 ? 'warning' : 'critical'
        }
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // NEW METHOD: Get system health counts for Step-3 monitoring
  // This replaces the problematic API endpoint that was causing "E.find is not a function"
  async getSystemHealthCounts() {
    try {
      // Get counts from database directly
      const [personsResult, arrestsResult, arresteesResult, rawResult, processingResult, completedResult] = await Promise.all([
        supabase?.from('persons')?.select('id', { count: 'exact', head: true }),
        supabase?.from('arrests')?.select('id', { count: 'exact', head: true }),
        supabase?.from('arrestee_records')?.select('id', { count: 'exact', head: true }),
        supabase?.from('ingest_raw')?.select('id', { count: 'exact', head: true }),
        supabase?.from('upload_batches')?.select('id', { count: 'exact', head: true })?.eq('status', 'processing'),
        supabase?.from('upload_batches')?.select('id', { count: 'exact', head: true })?.eq('status', 'completed')
      ]);

      // Handle any errors
      if (personsResult?.error) throw personsResult?.error;
      if (arrestsResult?.error) throw arrestsResult?.error;
      if (arresteesResult?.error) throw arresteesResult?.error;
      if (rawResult?.error) throw rawResult?.error;
      if (processingResult?.error) throw processingResult?.error;
      if (completedResult?.error) throw completedResult?.error;

      const counts = {
        persons_all: personsResult?.count || 0,
        arrests_all: arrestsResult?.count || 0,
        arrestee_records_all: arresteesResult?.count || 0,
        ingest_raw_all: rawResult?.count || 0,
        upload_processing: processingResult?.count || 0,
        upload_completed: completedResult?.count || 0
      };

      return { success: true, data: counts };
    } catch (error) {
      return { success: false, error: error?.message, data: {} };
    }
  }

  // Bulk requeue selected batches
  async requeueSelectedBatches(batchIds) {
    try {
      const results = [];
      
      for (const batchId of batchIds) {
        const result = await this.requeueBatch(batchId);
        results?.push({
          batchId,
          success: result?.success,
          error: result?.error
        });
      }

      const successCount = results?.filter(r => r?.success)?.length;
      const failureCount = results?.length - successCount;

      return {
        success: failureCount === 0,
        data: {
          total: results?.length,
          successful: successCount,
          failed: failureCount,
          results
        }
      };
    } catch (error) {
      return { success: false, error: error?.message };
    }
  }

  // Monitor batch progress (for live counters)
  async monitorBatchProgress(batchId, onProgress) {
    let monitoring = true;
    
    const checkProgress = async () => {
      if (!monitoring) return;

      try {
        const progression = await this.getBatchProgression(batchId);
        
        if (progression?.success && onProgress) {
          onProgress(progression?.data);
        }

        // Continue monitoring if batch is still processing
        const { data: batch } = await supabase
          ?.from('upload_batches')
          ?.select('status')
          ?.eq('id', batchId)
          ?.single();

        if (batch?.status === 'processing' || batch?.status === 'queued') {
          setTimeout(checkProgress, 2000); // Check every 2 seconds
        }
      } catch (error) {
        console.warn('Progress monitoring error:', error);
      }
    };

    // Start monitoring
    checkProgress();

    // Return stop function
    return () => {
      monitoring = false;
    };
  }
}

export default new BatchManagementService();